// Which Standard document a launch line actually asked for.
//
// POST /api/v1/standard/find-many takes a LIST of products and answers with the
// UNION of everything that matched any of them (eng-backend
// controller/standard/standard.js builds one `$or` branch per product). Picking
// a single product's standard back out of that union therefore has to use the
// same keys the backend filtered on — and COLOUR is one of them.
//
// Colour lives on the Standard: there is one document per colour (42 of them for
// FY alone, six colours × seven sections). Matching without it is why launching
// FY 1x2.5 in GREEN-YELLOW, BLACK and BLUE in one go produced three
// GREEN-YELLOW jobs — `find` handed all three cards the first document in the
// union, and the colour a job is built from is the STANDARD's, not the one typed
// into the form. The same launch of MYF 1x1.5 came out three times BLACK.
//
// COPPER_ROPE_* semi-finished standards carry no colour at all (find-many drops
// the colour filter for them), so a colour missing on either side is not a
// mismatch — only two colours that disagree are.
//
// Mirrored VERBATIM in eng-mobile/src/helpers/standardMatch.js;
// tests/launchChecksParity.test.js compares the SOURCE TEXT of the two and fails
// if they drift. The phone had this right first and the web did not — mobile's
// launchStandards.js even carried a note saying so — which is exactly the drift
// this pairing exists to stop.

// European locale uses the comma as decimal separator — normalise to a dot.
const toNum = v => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const U = v => String(v ?? '').trim().toUpperCase();

// The collection holds both real booleans and the strings "true"/"false", and
// non-mixed standards often omit the field entirely — see the `$or` in find-many.
const isTrue = v => v === true || v === 'true';

/** Does `standard` describe exactly the product the planner asked for? */
export function matchesStandard(product, standard) {
  if (U(standard?.name) !== U(product?.name)) return false;
  if (toNum(standard?.numberOfConductors) !== toNum(product?.numberOfConductors))
    return false;
  if (toNum(standard?.section) !== toNum(product?.section)) return false;

  const mixed = !!product?.hasMixedConductorSections;
  if (mixed !== isTrue(standard?.hasMixedConductorSections)) return false;
  if (mixed && toNum(standard?.greenYellowSection) !== toNum(product?.greenYellowSection))
    return false;

  const want = U(product?.color);
  const has = U(standard?.color);
  return !want || !has || want === has;
}

/** The standard for one product, out of a find-many union. `null` if absent. */
export function findStandardFor(standards, product) {
  return (standards || []).find(s => matchesStandard(product, s)) || null;
}

/**
 * What to put in front of somebody when no standard matched. The product label
 * deliberately carries no colour (helpers/productLabel.js), and a colour variant
 * that does not exist in the collection is the likeliest reason for a miss — so
 * the colour has to be in the message or the error is unactionable.
 */
export function describeLaunchLine(product = {}) {
  const core = `${product.numberOfConductors ?? ''}x${product.section ?? ''}`;
  const gy =
    product.hasMixedConductorSections && product.greenYellowSection
      ? `+${product.greenYellowSection}`
      : '';
  return [`${U(product.name)} ${core}${gy}`.trim(), U(product.color) || null]
    .filter(Boolean)
    .join(' ');
}
